/*----------------------------------
- DOMAINS LANDING PAGE
----------------------------------*/

/**
 * @docs docs/features/website-landing-experience
 * @fix  docs/fixes/2026-07-17-website-auth-conversion-resilience-regressions.md
 * @fix  docs/fixes/2026-09-21-signup-wall-repository-headline.md
 * @fix  docs/fixes/2026-09-21-filter-sheet-over-signup-modal.md
 * @rule The dataset context (the repository headline) is sent only for a GitHub visit. Every
 *       other click answers with the search on screen, and a row click with the row.
 * @rule Every click that opens authentication closes the filter sheet first. The sheet portals
 *       above the modal host, so an open sheet hides the signup modal it just triggered. The row
 *       preview drawer closes itself before its CTA hands off, for the same reason.
 */

import React from 'react';

import Badge from '@uniquedomains/components/ui/badge';
import { Header, type HeaderMobileActions } from '@/client/pages/landing/components/layout/Header';
import { useMediaQuery } from '@uniquedomains/components/runtime/useMediaQuery';
import type { RadarQuery } from '@uniquedomains/components/radar/rules/types';
import { buildPromptFilterChips } from '@uniquedomains/components/prompt/filters/promptFilters';
import useContext from '@/client/context';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import {
    DATABASE_GUEST_RESULTS_LIMIT,
    DATABASE_SEARCH_ACTIONS,
} from '@uniquedomains/components/contracts/search/searchAccessCatalog';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import HomeAskAiSection from '@/client/pages/landing/components/ask-ai/HomeAskAiSection';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import RelatedLinks from '@/client/pages/landing/components/navigation/RelatedLinks';
import {
    buildDomainsGeoContext,
    resolveDomainsGeoPathContext,
} from '@/client/pages/landing/components/geo/pageGeoContexts';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackConversionTrigger } from '@/client/pages/landing/lib/trackConversionTrigger';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import { decodeRadarFiltersUrl, encodeRadarFiltersUrl } from '@/common/libs/filters';
import { toAbsoluteProductUrlWithOrigin } from '@/client/utils/payment/productUrls';
import { buildWebsiteAuthHref, openWebsiteAuthHref } from '@/client/utils/conversion/authHref';
import { buildSearchConversionContext } from '@/client/services/conversion/intentContext';
import { readProductSession } from '@/client/services/conversion/product/productSession';
import { appErrorReporter } from '@/common/libs/errorReporting';
import type { TInputRadar } from '@/common/types/domains/radar';
import type * as Desired from '@/server/services/Conversion/Desired';
import type { AskYourAIPreviewRow, AskYourAIProps } from '@uniquedomains/components/ask-ai';
import type { WebsiteDomainsSearchRow } from '@/common/types/domains/websiteLanding';
import {
    resolveDomainsFullMarkdownMirrorPath,
    resolveDomainsMarkdownMirrorPath,
} from '@/client/pages/landing/domains/lib/aiMarkdownMirror';
import type { DomainsLandingPageProps } from './types';
import DomainsTitleBlock from './components/DomainsTitleBlock';
import DomainsFilterRail from './components/DomainsFilterRail';
import { useLazyComponent } from '@uniquedomains/components/runtime/useLazyComponent';
import PublicDomainsPreviewBlock from './components/PublicDomainsPreviewBlock';
import { buildDomainsBrowseChips } from './lib/browseChips';
import DomainsLandingCtaSection from './components/DomainsLandingCtaSection';
import { buildProductDomainUrl, buildProductFilterUrl, buildPublicRadarQuery } from './lib/publicRadarQuery';
import { EMPTY_DOMAINS_RESULTS_PREVIEW_ROWS } from '@/client/catalogs/landing/domains/emptyResultsPreviewRows';
import { formatPositiveCountStat } from '@/client/pages/landing/lib/formatCompactStat';
import type { ConversionTriggerId } from '@uniquedomains/components/contracts/analytics/conversionTriggers';
import type { WebsiteConversionContext } from '@uniquedomains/components/conversion/authIntent';
import type { WebsiteConversionWallAction } from '@uniquedomains/components/conversion/wallActions';
import {
    RADAR_CREATE_ACTION,
    WATCHLIST_ADD_ACTION,
} from '@uniquedomains/components/contracts/upgrade/catalogs/featureActions';
import {
    getGithubIntentExperience,
    readGithubIntent,
    resolveGithubIntentProductAuthentication,
    resolveGithubIntentOutcome,
    type GithubIntent,
} from './lib/githubIntent';
import { toWebsiteDomainsResultsTableSort, type WebsiteDomainsResultsSort } from './lib/resultsTableSort';
import { hasActiveAskAISearchFilters } from './askAiSearchFilters';

import '@/client/pages/landing/shared.css';
import './domains.css';

/*----------------------------------
- LAZY CHUNKS
----------------------------------*/

/*
 * The phone filter sheet is a `ui/modal`, which carries framer-motion. Imported statically it put
 * framer in `/domains`' eager chunks for every visitor, where it weighed on the phone LCP; it is
 * requested on the first Filters press instead (docs/fixes/2026-10-11-domains-mobile-lcp-eager-framer.md).
 */
const loadBrowseFilterSheet = () => import('@uniquedomains/components/radar/controls/BrowseFilterSheet');

/*----------------------------------
- CONSTANTS
----------------------------------*/

type WebsiteDomainsProductIntent = {
    trackingId: 'download' | 'create_radar' | 'see_all' | 'sort' | 'filter' | 'open_row' | 'add_row';
    desiredFeature: NonNullable<Desired.TDesired['feature']>;
    /* Typed to the wall allowlist: every click on this page is a wall (ADR-0050), and an id
       off the list would be dropped from the link and open the modal on generic copy. */
    desiredAction: WebsiteConversionWallAction;
    desiredPageOverride?: string;
    /**
     * Replaces the page's click context when the click names something narrower, a row. The
     * key's presence is what counts: a row click that builds no context still owns it.
     */
    contextOverride?: WebsiteConversionContext;
};

type DomainsAskAISearchFilterChip = {
    category: string;
    label: string;
    value: string;
};

type DomainsAskAISearchFilters = Record<string, string | DomainsAskAISearchFilterChip[]>;

const toAskAIPreviewRows = (rows: WebsiteDomainsSearchRow[]): AskYourAIPreviewRow[] =>
    (rows || []).slice(0, 3).map((row) => ({
        name: row.domain,
        ...(row.tld ? { tld: row.tld } : {}),
        ...(row.status ? { status: row.status } : {}),
        ...(typeof row.askPrice === 'number' ? { pricePurchase: row.askPrice } : {}),
        ...(typeof row.renewal === 'number' ? { priceRenewal: row.renewal } : {}),
        ...(row.brandabilityBand ? { brandabilityBand: row.brandabilityBand } : {}),
        ...(row.demandBand ? { demandBand: row.demandBand } : {}),
        ...(row.risk ? { risk: row.risk } : {}),
    }));

const buildAskAISearchFilters = (
    filters: Partial<TInputRadar>,
    filterChips: DomainsAskAISearchFilterChip[],
    sort: WebsiteDomainsResultsSort | null,
): DomainsAskAISearchFilters => {
    const searchFilters: DomainsAskAISearchFilters = {};
    const prompt = filters.prompt?.trim();

    if (prompt) {
        searchFilters.prompt = prompt;
    }

    if (filterChips.length) {
        searchFilters.chips = filterChips.map((chip) => ({
            category: chip.category,
            label: chip.label,
            value: chip.value,
        }));
    }

    if (sort) {
        searchFilters.sort = `${sort.id}:${sort.order}`;
    }

    return searchFilters;
};

const DOMAINS_TRIGGER_BY_INTENT: Record<WebsiteDomainsProductIntent['trackingId'], ConversionTriggerId> = {
    add_row: 'website_domains_add_row',
    create_radar: 'website_domains_create_radar',
    download: 'website_domains_download',
    filter: 'website_domains_filter',
    open_row: 'website_domains_open_row',
    see_all: 'website_domains_see_all',
    sort: 'website_domains_sort',
};

const getGithubIntentTriggerId = (intent: GithubIntent, isLoggedIn: boolean): ConversionTriggerId => {
    if (isLoggedIn) {
        return intent === 'project'
            ? 'website_domains_github_intent_project_logged_in'
            : 'website_domains_github_intent_radar_logged_in';
    }

    return intent === 'project'
        ? 'website_domains_github_intent_project_anonymous'
        : 'website_domains_github_intent_radar_anonymous';
};

/*----------------------------------
- COMPONENT
----------------------------------*/

const DomainsLandingPage = ({
    browseResources,
    currentPath,
    initialFilters,
    navigation,
    search,
    projection,
    filterCatalog,
    totalTlds,
}: DomainsLandingPageProps) => {
    const { user, publicOrigins } = useContext();
    const githubIntentHandledRef = React.useRef(false);
    const [githubIntent, setGithubIntent] = React.useState<GithubIntent | null>(null);
    const [isFilterSheetOpen, setIsFilterSheetOpen] = React.useState(false);
    /*
     * The rail's query is frozen by design, so a never-applied draft or an open picker must not
     * survive the hand-off to authentication. Bumping this remounts the rail back to the search
     * actually on screen.
     */
    const [railKey, setRailKey] = React.useState(0);
    /*
     * A search with no results draws illustrative placeholder rows, so the page never shows an
     * empty table; the block keeps their click on See all rather than previewing a name the visitor
     * never really saw listed.
     */
    const rowsArePlaceholders = !search?.list?.length;
    const previewRows = React.useMemo(
        () => (rowsArePlaceholders ? EMPTY_DOMAINS_RESULTS_PREVIEW_ROWS : search.list).slice(0, DATABASE_GUEST_RESULTS_LIMIT),
        [rowsArePlaceholders, search?.list],
    );
    const effectiveFilters = search?.effectiveFilters || initialFilters;
    const githubExperience = React.useMemo(() => getGithubIntentExperience(githubIntent), [githubIntent]);
    const publicPersonaMode = githubExperience.primaryAction.persona;
    const heroTitle = search?.seo?.page?.h1?.trim() || search?.humanized?.trim() || 'One-Word Domains';
    const browseLinks = browseResources.map((resource) => ({
        label: resource.label,
        href: resource.href,
    }));
    // Sectors only: a TLD's page is its `/tld/<slug>` insight page since ADR-0054, which carries
    // its own sibling links, and Product lists browse siblings of the current kind alone.
    const browseLinksTitle = 'Browse more sectors';
    const productHref = toAbsoluteProductUrlWithOrigin(
        encodeRadarFiltersUrl(effectiveFilters, {
            surface: 'product',
        }),
        publicOrigins.productOrigin,
    );
    /* Memoized since the click context now depends on it: a fresh array on every render
       would rebuild the context, and with it every CTA handler, for nothing. */
    const filterChips = React.useMemo(() => buildPromptFilterChips(effectiveFilters), [effectiveFilters]);
    /* A repository slug on the page names the repo only to a visitor who came from it:
       `githubIntent` is set on mount from `?github_intent=` and stays for the whole visit. */
    const conversionContext = React.useMemo(
        () => buildSearchConversionContext({ search, filterChips, heroTitle, cameFromGithub: Boolean(githubIntent) }),
        [filterChips, githubIntent, heroTitle, search],
    );
    /*
     * The GitHub intent effect runs in the same mount pass that reads the intent, before
     * `githubIntent` reaches state, so `conversionContext` still reads as a non-GitHub visit
     * there. The effect only acts when the URL carries the intent, which is exactly the
     * GitHub visit, so it gets its own context built as one.
     */
    const githubConversionContext = React.useMemo(
        () => buildSearchConversionContext({ search, filterChips, heroTitle, cameFromGithub: true }),
        [filterChips, heroTitle, search],
    );
    const currentSort = toWebsiteDomainsResultsTableSort(effectiveFilters.sort);
    const hasAskAISearchFilters = hasActiveAskAISearchFilters(effectiveFilters, filterChips, currentSort);
    const askAIPathContext = React.useMemo(
        () => resolveDomainsGeoPathContext(currentPath, hasAskAISearchFilters),
        [currentPath, hasAskAISearchFilters],
    );
    const markdownMirrorPath = React.useMemo(() => resolveDomainsMarkdownMirrorPath(currentPath), [currentPath]);
    const fullMarkdownMirrorPath = React.useMemo(
        () => resolveDomainsFullMarkdownMirrorPath(currentPath),
        [currentPath],
    );
    const pageGeoContext = React.useMemo(
        () =>
            buildDomainsGeoContext({
                currentPath,
                fullMarkdownMirrorPath,
                hasSearchFilters: hasAskAISearchFilters,
                markdownMirrorPath,
                previewRows: search?.list || [],
                searchHash: search?.searchId,
                searchSummary: search?.humanized || heroTitle,
                searchUrl: search?.url,
                sector: askAIPathContext.sector,
                tld: askAIPathContext.tld,
            }),
        [
            askAIPathContext.sector,
            askAIPathContext.tld,
            currentPath,
            fullMarkdownMirrorPath,
            hasAskAISearchFilters,
            heroTitle,
            markdownMirrorPath,
            search?.humanized,
            search?.list,
            search?.searchId,
            search?.url,
        ],
    );
    const askAIContext = React.useMemo<AskYourAIProps>(() => {
        const cta =
            askAIPathContext.pageType === 'domains_index'
                ? 'open_database'
                : askAIPathContext.pageType === 'sector' && publicPersonaMode === 'founder'
                  ? 'start_project'
                  : 'create_radar';
        const intent = cta === 'start_project'
            ? 'start_project'
            : cta === 'open_database'
              ? 'evaluate_product'
              : 'create_radar';

        return {
            pageType: askAIPathContext.pageType,
            intent,
            pageUrl: search?.url || currentPath,
            canonicalPageUrl: pageGeoContext.canonicalUrl,
            pageTitle: heroTitle,
            persona: publicPersonaMode,
            ...(askAIPathContext.tld ? { tld: askAIPathContext.tld } : {}),
            ...(askAIPathContext.sector ? { sector: askAIPathContext.sector } : {}),
            searchSummary: search?.humanized || heroTitle,
            searchFilters: buildAskAISearchFilters(effectiveFilters, filterChips, currentSort),
            searchHash: search?.searchId,
            previewRows: toAskAIPreviewRows(search?.list || []),
            geoContext: pageGeoContext,
            cta,
        };
    }, [
        askAIPathContext.pageType,
        askAIPathContext.sector,
        askAIPathContext.tld,
        currentPath,
        currentSort,
        effectiveFilters,
        filterChips,
        heroTitle,
        pageGeoContext,
        publicPersonaMode,
        search?.humanized,
        search?.list,
        search?.searchId,
        search?.url,
    ]);
    const isLoggedIn = Boolean(user?.email);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;
        setGithubIntent(readGithubIntent(window.location.href));
    }, []);

    React.useEffect(() => {
        track('page_view', {
            page: 'domains_public',
            path: currentPath,
            persona: publicPersonaMode,
            has_results: Boolean(search?.list.length),
        });
    }, [currentPath, publicPersonaMode, search?.list.length]);

    /* The page carries the siblings' reveal transitions; `initScrollReveal` switches them on. */
    React.useEffect(() => {
        if (typeof window === 'undefined') return;

        void import('@/client/pages/landing/lib/reveal').then(({ initScrollReveal }) => {
            initScrollReveal();
        });
    }, []);

    React.useEffect(() => {
        if (githubIntentHandledRef.current || typeof window === 'undefined') return;

        const outcome = resolveGithubIntentOutcome({
            inputUrl: window.location.href,
            productSearchUrl: productHref,
            /* Same context every manual CTA sends once `githubIntent` is in state, so the
               modal that opens by itself and the one opened by a click describe the same
               visit. On a repo page this is the dataset context naming the repository. */
            context: githubConversionContext,
        });
        if (!outcome) return;

        githubIntentHandledRef.current = true;
        setGithubIntent(outcome.intent);
        window.history.replaceState(null, '', outcome.cleanedUrl);
        const continueGithubIntent = async () => {
            let productSessionIsAuthenticated = isLoggedIn;
            try {
                productSessionIsAuthenticated = await resolveGithubIntentProductAuthentication(
                    isLoggedIn,
                    () => readProductSession(publicOrigins.productOrigin),
                );
            } catch (sessionError) {
                appErrorReporter.app.handleError(sessionError);
                console.warn(
                    '[github-intent] Product session classification failed, continuing anonymously.',
                    sessionError,
                );
            }

            trackConversionTrigger(getGithubIntentTriggerId(outcome.intent, productSessionIsAuthenticated), {
                action: outcome.desired.action || null,
                feature: outcome.desired.feature || null,
                href: outcome.href,
                interaction: 'auto',
                page: outcome.desired.page || null,
                section: 'github_intent',
            });

            openWebsiteAuthHref(outcome.href);
        };

        void continueGithubIntent();
    }, [githubConversionContext, isLoggedIn, productHref]);

    const navigateToProduct = React.useCallback(
        (intent: WebsiteDomainsProductIntent) => {
            if (typeof window === 'undefined') return;

            const desiredPage = intent.desiredPageOverride || productHref;
            const authHref = buildWebsiteAuthHref({
                flow: 'cold',
                mode: 'signup',
                /* No persona on any click from this page, including a GitHub intent.
                   `publicPersonaMode` describes how the page renders, which is a fact about
                   the page and not a declaration by the visitor, and it defaults to
                   investor: in the week of 2026-08-27 that default supplied the persona for
                   most signups and sent them down the paywalled path. The account screen
                   asks instead. The search or dataset context still travels below. */
                returnTo: desiredPage,
                /* `in`, not `??`: a row click on a search with no title and no hero title
                   builds no context at all, and `??` would then fall back to the page context,
                   which on a GitHub visit is the dataset context, the repository headline a
                   row click must never send. A click that sets the key owns the context, even
                   when it is empty. See docs/fixes/2026-09-21-signup-wall-repository-headline.md. */
                context: 'contextOverride' in intent ? intent.contextOverride : conversionContext,
                feature: intent.desiredFeature,
                action: intent.desiredAction,
                from: `website_domains_${intent.trackingId}`,
            });
            trackLandingCta({
                page: 'domains_public',
                section: 'domains_results',
                label: intent.trackingId,
                kind: intent.trackingId === 'create_radar' ? 'radar' : 'product',
                href: authHref,
                feature: intent.desiredFeature,
                action: intent.desiredAction,
                surface: 'results',
            });
            trackConversionTrigger(DOMAINS_TRIGGER_BY_INTENT[intent.trackingId], {
                action: intent.desiredAction,
                feature: intent.desiredFeature,
                href: authHref,
                page: desiredPage,
                section: 'domains_results',
            });

            track(intent.trackingId === 'create_radar' ? 'cta_create_radar_click' : 'cta_open_screener_click', {
                page: 'domains_public',
                persona: publicPersonaMode,
                intent: intent.trackingId,
                feature: intent.desiredFeature,
                action: intent.desiredAction,
            });

            /*
             * Closed before the modal opens, for every click and not only the two the sheet
             * hosts: one place covers `handleFilterIntent`, `handleLockedFilter` and any
             * control added to the sheet later. The sheet portals at z-index 2147483646 while
             * the signup modal renders in Proteum's `#modals` host, capped at 200, so a sheet
             * left open covers the modal it triggered and the visitor sees nothing happen.
             * Closing a sheet that is not open is a no-op. See
             * docs/fixes/2026-09-21-filter-sheet-over-signup-modal.md.
             *
             * The row preview drawer needs no clearing here: it portals above that same host too,
             * but its CTA closes it before calling `handleUnlockRow` (`PublicDomainRowDrawer`), and
             * while it is open its overlay is the only thing a visitor can click.
             */
            setIsFilterSheetOpen(false);
            openWebsiteAuthHref(authHref);
        },
        [conversionContext, githubIntent, productHref, publicPersonaMode],
    );
    const handleSeeAll = React.useCallback(() => {
        navigateToProduct({
            trackingId: 'see_all',
            desiredFeature: 'databaseAccess',
            desiredAction: DATABASE_SEARCH_ACTIONS.seeAll,
        });
    }, [navigateToProduct]);
    /**
     * The drawer's CTA names the row: the wall headline quotes the domain, `from` records
     * `website_domains_open_row` so a signup that opened a name is told apart from See all, and
     * `returnTo` is the visitor's own search carrying `?domain=`, so Browse opens that row's
     * panel over those results when it loads the row and shows the plain list when it does not.
     */
    const handleUnlockRow = React.useCallback(
        (domain: string) => {
            navigateToProduct({
                trackingId: 'open_row',
                desiredFeature: 'databaseAccess',
                desiredAction: DATABASE_SEARCH_ACTIONS.unlockDomainDetails,
                desiredPageOverride: buildProductDomainUrl(productHref, domain),
                contextOverride: buildSearchConversionContext({
                    search,
                    filterChips,
                    heroTitle,
                    cameFromGithub: Boolean(githubIntent),
                    domain,
                }),
            });
        },
        [filterChips, githubIntent, heroTitle, navigateToProduct, productHref, search],
    );
    /**
     * The row "+" saves a name to a Watchlist, a Full Access tool, so it names that gate
     * (`website_domains_add_row`, `add-domain`) instead of reading as See all as it did until
     * 2026-09-28. It lands on the same row URL as the drawer CTA: the name the visitor wanted
     * to keep is open when they arrive.
     */
    const handleAddRow = React.useCallback(
        (domain: string) => {
            navigateToProduct({
                trackingId: 'add_row',
                desiredFeature: 'watchlistCount',
                desiredAction: WATCHLIST_ADD_ACTION,
                desiredPageOverride: buildProductDomainUrl(productHref, domain),
                contextOverride: buildSearchConversionContext({
                    search,
                    filterChips,
                    heroTitle,
                    cameFromGithub: Boolean(githubIntent),
                    domain,
                }),
            });
        },
        [filterChips, githubIntent, heroTitle, navigateToProduct, productHref, search],
    );
    const handleOpenFilterSheet = React.useCallback(() => {
        setIsFilterSheetOpen(true);
    }, []);
    const handleCloseFilterSheet = React.useCallback(() => {
        setIsFilterSheetOpen(false);
    }, []);
    /* Null until the first Filters press; a failed chunk closes the sheet so the next press retries. */
    const FilterSheet = useLazyComponent(loadBrowseFilterSheet, isFilterSheetOpen, handleCloseFilterSheet);
    const handleDownload = React.useCallback(() => {
        navigateToProduct({
            trackingId: 'download',
            desiredFeature: 'exportRowsCount',
            /* `unlock-database` until 2026-09-28, which recorded a Download as a generic
               database unlock and gave the wall nothing to name. */
            desiredAction: DATABASE_SEARCH_ACTIONS.download,
        });
    }, [navigateToProduct]);
    /**
     * The button reads Create radar, so its tracking id does too: `from` and the legacy event
     * then name the action the visitor clicked rather than the Save search control it replaced.
     */
    const handleCreateRadar = React.useCallback(() => {
        navigateToProduct({
            trackingId: 'create_radar',
            desiredFeature: 'radarsCount',
            desiredAction: RADAR_CREATE_ACTION,
        });
    }, [navigateToProduct]);
    /**
     * Ordering is a member tool, so the public control opens authentication instead.
     *
     * It never writes the URL: a `?sort=` would turn this page into a faceted state, which
     * `resolveDomainsRobotsDirectives` answers with `noindex`, and the crawlable listing is the
     * point of the page.
     */
    const handleSort = React.useCallback(() => {
        navigateToProduct({
            trackingId: 'sort',
            desiredFeature: 'databaseAccess',
            desiredAction: DATABASE_SEARCH_ACTIONS.sort,
        });
    }, [navigateToProduct]);
    /**
     * Narrowing is a member tool, so a rule change opens authentication instead of filtering.
     *
     * The intended query travels in `returnTo`, and like Sort this never writes the URL: a
     * `?rules=` would turn the page into a faceted state that `resolveDomainsRobotsDirectives`
     * answers with `noindex` (ADR-0035).
     */
    const handleFilterIntent = React.useCallback(
        (nextQuery: RadarQuery) => {
            navigateToProduct({
                trackingId: 'filter',
                desiredFeature: 'databaseAccess',
                desiredAction: DATABASE_SEARCH_ACTIONS.filter,
                desiredPageOverride: toAbsoluteProductUrlWithOrigin(
                    buildProductFilterUrl(effectiveFilters, nextQuery.rules),
                    publicOrigins.productOrigin,
                ),
            });
            setRailKey((key) => key + 1);
        },
        [effectiveFilters, navigateToProduct, publicOrigins.productOrigin],
    );
    /** A locked accordion names the plan gate rather than the search, so it sends its own action. */
    const handleLockedFilter = React.useCallback(() => {
        navigateToProduct({
            trackingId: 'filter',
            desiredFeature: 'databaseAccess',
            desiredAction: DATABASE_SEARCH_ACTIONS.premiumFilter,
        });
        setRailKey((key) => key + 1);
    }, [navigateToProduct]);

    const countPrecision = search?.stats?.countPrecision ?? 'exact';
    const totalCountLabel = formatPositiveCountStat(search?.stats?.count, countPrecision);
    const medianAsk = React.useMemo(() => {
        const value = search?.stats?.medianAsk;
        return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : null;
    }, [search?.stats?.medianAsk]);
    const railQuery = React.useMemo(() => buildPublicRadarQuery(effectiveFilters), [effectiveFilters]);
    const intro = search?.seo?.page?.intro_paragraph?.trim() || null;
    /*
     * One rail instance only: two would duplicate every `radar-rule-panel-<id>` element id.
     * SSR takes the desktop branch, like the product's own shell.
     */
    const isMobileRail = useMediaQuery('(max-width: 1279.98px)');
    const browseChips = React.useMemo(() => buildDomainsBrowseChips(currentPath), [currentPath]);
    /* The phone bar's left slot opens the same sheet the title row opens between 1024 and 1280,
       so the two never coexist and the listing keeps one Filters affordance at every width. */
    const mobileActions = React.useMemo<HeaderMobileActions>(
        () => ({
            secondary: {
                label: 'Filters',
                iconName: 'sliders',
                badge: railQuery.rules.length,
                onClick: handleOpenFilterSheet,
                testId: 'domains-filters-open',
            },
        }),
        [handleOpenFilterSheet, railQuery.rules.length],
    );
    const filterRailNode = (
        <DomainsFilterRail
            key={railKey}
            filterCatalog={filterCatalog}
            query={railQuery}
            onFilterIntent={handleFilterIntent}
            onLockedFilter={handleLockedFilter}
        />
    );
    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">

            <div class="relative z-10">
                {/* The header matches this against its nav hrefs, so the query string stays out. */}
                <Header
                    links={LANDING_WEBSITE_LINKS}
                    navigation={navigation}
                    currentPath={currentPath.split('?')[0]}
                    trackingPage="domains_public"
                    mobileActions={mobileActions}
                />

                <main id="top">
                    <div class="ud-public-domains-band pt-[40px]">
                        <DomainsTitleBlock
                            heroTitle={heroTitle}
                            intro={intro}
                            browseChips={browseChips}
                            activeFilterCount={railQuery.rules.length}
                            onOpenFilters={handleOpenFilterSheet}
                            onDownload={handleDownload}
                            onSort={handleSort}
                            onCreateRadar={handleCreateRadar}
                        />
                    </div>

                    {githubExperience.banner ? (
                        <section class="ud-public-domains-band pt-6">
                            <div class="mx-auto w-full max-w-[760px] rounded-[28px] border border-amber-200/70 bg-amber-50/90 px-5 py-4 text-center shadow-[0_18px_48px_rgba(146,64,14,0.08)]">
                                <div class="flex flex-wrap items-center justify-center gap-2">
                                    <Badge variant="amber">{githubExperience.banner.eyebrow}</Badge>
                                    <Badge variant="dark">
                                        {githubExperience.primaryAction.persona === 'investor' ? 'Investor' : 'Founder'}{' '}
                                        · {githubExperience.primaryAction.planLabel}
                                    </Badge>
                                </div>
                                <p class="mt-3 text-base font-semibold tracking-[-0.03em] text-slate-950">
                                    {githubExperience.banner.title}
                                </p>
                                <p class="mt-2 text-sm leading-6 text-slate-600">
                                    {githubExperience.banner.description}
                                </p>
                            </div>
                        </section>
                    ) : null}

                    <div class="ud-public-domains-band ud-public-domains-layout pt-[26px]">
                        {isMobileRail ? null : (
                            <aside class="ud-public-domains-rail" aria-label="Filters">
                                <div class="ud-public-domains-rail__scroll">{filterRailNode}</div>
                            </aside>
                        )}

                        <div class="min-w-0">
                            <section class="ud-public-domains-table-section">
                                <PublicDomainsPreviewBlock
                                    rows={previewRows}
                                    projection={projection}
                                    totalCountLabel={totalCountLabel}
                                    countPrecision={countPrecision}
                                    rowsArePlaceholders={rowsArePlaceholders}
                                    page="domains_public"
                                    section="domains_results"
                                    triggerId="website_domains_row_preview"
                                    onSeeAll={handleSeeAll}
                                    onUnlockRow={handleUnlockRow}
                                    onAddRow={handleAddRow}
                                />
                            </section>
                        </div>
                    </div>

                    {isMobileRail && FilterSheet ? (
                        /* The CTA says Done rather than counting down to results: nothing in this
                           rail changes the rows (ADR-0035), so there is no count to promise. */
                        <FilterSheet
                            open={isFilterSheetOpen}
                            onClose={handleCloseFilterSheet}
                            activeFilterCount={railQuery.rules.length}
                            ctaLabel="Done"
                        >
                            {filterRailNode}
                        </FilterSheet>
                    ) : null}

                    <RelatedLinks
                        className="max-w-[1240px] pt-12 lg:pt-16"
                        links={browseLinks}
                        title={browseLinksTitle}
                    />

                    <DomainsLandingCtaSection
                        medianAsk={medianAsk}
                        totalCountLabel={totalCountLabel}
                        countPrecision={countPrecision}
                        totalTlds={totalTlds}
                    />
                </main>

                <HomeAskAiSection context={askAIContext} />

                <PageGeoBlock
                    className="mx-auto max-w-[1240px] px-4 pb-2 pt-8 sm:px-6 lg:px-8 [&_details]:border-t-0 [&_details]:pt-0"
                    geo={pageGeoContext}
                    surface="domains_public"
                />

                <div class="mx-auto max-w-[1240px] px-4 pt-8 sm:px-6 lg:px-8">
                    <div class="h-px w-full bg-[var(--color-card-2)]" />
                </div>

                <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage="domains_public" />
            </div>
        </div>
    );
};

export default DomainsLandingPage;
